import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { AuthProvider } from '@/api/auth-context.js';
import { setUnauthorizedHandler } from '@/api/client.js';
import { AppLayout } from '@/components/AppLayout.js';
import { HandshakeContextProvider } from '@/components/HandshakeContext.js';
import { HandshakeGate } from '@/components/HandshakeGate.js';
import { HostNavigationSync } from '@/components/HostNavigationSync.js';
import { TenantTheme } from '@/components/TenantTheme.js';
import { Toaster } from '@/components/ui/sonner.js';
import { useHandshake } from '@/handshake/useHandshake.js';
import { FlowsPage } from '@/pages/flows.js';
import { RunsPage } from '@/pages/runs.js';
import { RunDetailPage } from '@/pages/run-detail.js';
import { SettingsPage } from '@/pages/settings.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

export function App() {
  const handshake = useHandshake();

  // When any API call gets a 401, re-run the auth flow (refresh the token, or
  // fall back to a full re-login). Registered globally because `apiFetch` and
  // the `QueryClient` live outside React.
  const { requestTokenRefresh, jwt } = handshake;
  useEffect(() => {
    setUnauthorizedHandler(requestTokenRefresh);
    return () => setUnauthorizedHandler(null);
  }, [requestTokenRefresh]);

  // Once a fresh JWT lands (after a refresh), refetch everything so the queries
  // that failed with 401 retry with the new token. Skip the initial sign-in.
  const previousJwt = useRef<string | null>(null);
  useEffect(() => {
    if (jwt && previousJwt.current && previousJwt.current !== jwt) {
      void queryClient.invalidateQueries();
    }
    previousJwt.current = jwt;
  }, [jwt]);

  return (
    <QueryClientProvider client={queryClient}>
      <HandshakeContextProvider value={handshake}>
        <HandshakeGate handshake={handshake}>
          <AuthProvider jwt={handshake.jwt} requestTokenRefresh={handshake.requestTokenRefresh}>
            <TenantTheme>
              <BrowserRouter>
                <HostNavigationSync />
                <Routes>
                  <Route element={<AppLayout />}>
                    <Route index element={<Navigate to="/runs" replace />} />
                    <Route path="/runs" element={<RunsPage />} />
                    <Route path="/runs/:runId" element={<RunDetailPage />} />
                    <Route path="/flows" element={<FlowsPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="*" element={<Navigate to="/runs" replace />} />
                  </Route>
                </Routes>
              </BrowserRouter>
              <Toaster />
            </TenantTheme>
          </AuthProvider>
        </HandshakeGate>
      </HandshakeContextProvider>
    </QueryClientProvider>
  );
}
